import React, { useEffect } from 'react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { SEOHero, SEOSection, FeatureGrid, BenefitList, InternalLinkGrid, CTABanner } from '../../components/SEOPageComponents';
import { Globe, Receipt, Shield, Zap, RefreshCw, BarChart3, Wifi } from 'lucide-react';

export default function ChannelManagerEInvoice() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="Channel Manager e-Invoice Integration — Automate OTA Booking Invoices"
        description="StayMii integrates with your channel manager to automate LHDN e-invoicing for OTA bookings from Booking.com, Agoda, Expedia, and more. Sync bookings and generate compliant invoices automatically."
        keywords="channel manager e invoice, channel manager e invoice integration, ota e invoice, booking.com e invoice, agoda e invoice, expedia e invoice, channel manager lhdn, ota invoicing malaysia"
        canonicalUrl="/channel-manager-e-invoice"
      />
      <div className="min-h-screen bg-white">
        <LandingNavBar />
        <SEOHero
          badge="Channel Manager"
          h1="Channel Manager e-Invoice: Automate OTA Booking Invoices for LHDN"
          subtitle="Sync bookings from Booking.com, Agoda, Expedia, and other OTAs via your channel manager. StayMii auto-generates LHDN-compliant e-invoices for every reservation — no manual work required."
        />

        <SEOSection h2="Why Channel Manager e-Invoice Integration Matters" description="Hotels using channel managers receive bookings from multiple OTAs. Each booking needs a compliant e-invoice for LHDN. Manual processing is impossible at scale — StayMii automates it.">
          <FeatureGrid items={[
            { icon: Globe, title: 'Multi-OTA Booking Sync', desc: 'Automatically capture bookings from Booking.com, Agoda, Expedia, Airbnb, and other OTAs through your channel manager.' },
            { icon: Receipt, title: 'Auto-Generate e-Invoices', desc: 'Every OTA booking is converted into a validated LHDN-compliant e-invoice with correct tax calculations.' },
            { icon: RefreshCw, title: 'Real-Time Sync', desc: 'Bookings are synced in real-time as they come in. No batch delays, no missed invoices.' },
            { icon: Shield, title: 'LHDN Compliant', desc: 'All e-invoices meet LHDN MyInvois specifications. SST, service charge, and tourism tax handled automatically.' },
            { icon: BarChart3, title: 'OTA Revenue Tracking', desc: 'Track revenue by OTA channel with real-time analytics. Know which channels drive the most compliant revenue.' },
            { icon: Zap, title: 'Zero Configuration', desc: 'StayMii connects to your channel manager API. No changes to your existing booking flow.' },
          ]} />
        </SEOSection>

        <SEOSection h2="Supported OTA Channels" bg="bg-slate-50" description="StayMii processes e-invoices for bookings from all major OTAs synced through your channel manager:">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {['Booking.com', 'Agoda', 'Expedia', 'Airbnb', 'Trip.com', 'Traveloka', 'Hotels.com', 'Direct Bookings'].map((ota, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                <p className="text-sm font-bold text-slate-800">{ota}</p>
                <p className="text-xs text-slate-400 mt-1">e-Invoice automated</p>
              </div>
            ))}
          </div>
        </SEOSection>

        <SEOSection h2="Related Solutions">
          <InternalLinkGrid links={[
            { to: '/pms-e-invoice-integration', title: 'PMS e-Invoice Integration', desc: 'Connect your hotel PMS' },
            { to: '/hotel-e-invoice-system', title: 'Hotel e-Invoice System', desc: 'Complete hotel e-invoicing' },
            { to: '/airbnb-e-invoice-malaysia', title: 'Airbnb e-Invoice Malaysia', desc: 'Short-term rental compliance' },
            { to: '/e-invoice-malaysia', title: 'e-Invoice Malaysia', desc: 'Malaysia e-invoicing guide' },
            { to: '/lhdn-e-invoice', title: 'LHDN e-Invoice', desc: 'Compliance requirements' },
            { to: '/myinvois-api', title: 'MyInvois API', desc: 'API integration details' },
          ]} />
        </SEOSection>

        <CTABanner title="Automate e-Invoices for Every OTA Booking" subtitle="Connect your channel manager to StayMii and never create a manual invoice again." />
        <LandingFooter />
      </div>
    </>
  );
}
